/**
 * client.ts — thin api wrapper for /api/notifications/* endpoints
 * (PRD §07 In-App Bell server contract).
 *
 * No caching here — TanStack Query owns that layer. This module
 * only exists so call sites don't repeat URL strings and so the
 * envelope shapes stay typed.
 */
import { apiGet, apiPost } from '@/api/client';
import type {
  NotificationListEnvelope,
  UnreadCountEnvelope,
  MarkAllReadEnvelope,
  ReadDismissEnvelope,
} from './types';

export const notificationsApi = {
  /** GET /api/notifications?limit=N — caller-scoped, excludes dismissed. */
  list(limit = 20): Promise<NotificationListEnvelope> {
    return apiGet<NotificationListEnvelope>(`/notifications?limit=${limit}`);
  },

  /** GET /api/notifications/unread-count — lightweight badge query. */
  unreadCount(): Promise<UnreadCountEnvelope> {
    return apiGet<UnreadCountEnvelope>('/notifications/unread-count');
  },

  /** POST /api/notifications/:id/read — idempotent mark-read for one row. */
  markRead(notificationId: number): Promise<ReadDismissEnvelope> {
    return apiPost<ReadDismissEnvelope>(`/notifications/${notificationId}/read`, {});
  },

  /** POST /api/notifications/mark-all-read — bulk flip caller's unread rows. */
  markAllRead(): Promise<MarkAllReadEnvelope> {
    return apiPost<MarkAllReadEnvelope>('/notifications/mark-all-read', {});
  },

  /** POST /api/notifications/:id/dismiss — remove from bell, keep in history. */
  dismiss(notificationId: number): Promise<ReadDismissEnvelope> {
    return apiPost<ReadDismissEnvelope>(`/notifications/${notificationId}/dismiss`, {});
  },
};
